import React from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageWrapper from 'components/Common/PageWraper';
import DissectionPie, { PieDataItem } from './DissectionPie';
import { Column } from 'react-data-grid';
import { CustomizedDataGrid } from 'components/customized_data_grid';

interface CloudVendorRow {
    id: string;
    cloudVendor: string;
    fy2026: string;
    isParent?: boolean;
}
 
const renderCloudVendorCell = (key: keyof CloudVendorRow) => ({ row }: { row: CloudVendorRow }) => {
    const isBold = row.isParent || row.id === '1';
    return (
        <span style={{ fontWeight: isBold ? 700 : 400 }}>
            {row[key]}
        </span>
    );
};
 
const cloudVendorColumns: Column<CloudVendorRow>[] = [
    {
        key: 'cloudVendor',
        name: 'Cloud Vendor',
        minWidth: 180,
        formatter: renderCloudVendorCell('cloudVendor'),
    },
    {
        key: 'fy2026',
        name: 'FY2026 (6+6)',
        minWidth: 140,
        formatter: renderCloudVendorCell('fy2026'),
    },
];
 
const cloudVendorDefaultColumnNames = ['Cloud Vendor', 'FY2026 (6+6)'];
 
const cloudVendorTableData: CloudVendorRow[] = [
    { id: '1', cloudVendor: 'D&AI Total', fy2026: '96.5', isParent: true },
    { id: '2', cloudVendor: 'Azure Total', fy2026: '93.6' },
    { id: '3', cloudVendor: 'AWS Total', fy2026: '2.7' },
    { id: '4', cloudVendor: 'GCP Total', fy2026: '0.3' },
];

/* ─── Section 1 Data: Azure, AWS & GCP Dissection ───────────────── */

const azurePieData: PieDataItem[] = [
	{ name: 'Databricks', value: 47.0 },
	{ name: 'Storage', value: 11.1 },
	{ name: 'AKS', value: 8.2 },
	{ name: 'DataFactory', value: 3.5 },
	{ name: 'Network', value: 1.9 },
	{ name: 'Synapse', value: 1.7 },
	{ name: 'NetApp', value: 1.7 },
	{ name: 'Sql', value: 1.4 },
	{ name: 'Other Services*', value: 17.1 },
];

const awsPieData: PieDataItem[] = [
	{ name: 'AmazonEC2', value: 821 },
	{ name: 'Securiti Platform Private Offer', value: 414 },
	{ name: 'AmazonRDS', value: 301 },
	{ name: 'NVIDIA AI Enterprise', value: 241 },
	{ name: 'IBM Kubecost Enterprise', value: 198 },
	{ name: 'Temporal Cloud', value: 150 },
	{ name: 'AWSSupportEnterprise', value: 114 },
	{ name: 'Other Services', value: 425 },
];

const gcpPieData: PieDataItem[] = [
	{ name: 'Support', value: 94 },
	{ name: 'Route Optimization API', value: 86 },
	{ name: 'Cloud Logging', value: 61 },
	{ name: 'Vertex AI', value: 13 },
	{ name: 'Other Services', value: 26 },
];

/* ─── Section 2 Data: Prod & Non-Prod ───────────────────────────── */

const prodPieData: PieDataItem[] = [
	{ name: 'Databricks', value: 12.21 },
	{ name: 'Storage', value: 1.94 },
	{ name: 'DataFactory', value: 1.29 },
	{ name: 'Compute', value: 0.66 },
	{ name: 'NetApp', value: 0.47 },
	{ name: 'Network', value: 0.42 },
	{ name: 'OperationalInsights', value: 0.32 },
	{ name: 'Other Services', value: 0.82 },
];

const nonProdPieData: PieDataItem[] = [
	{ name: 'Databricks', value: 4.04 },
	{ name: 'Storage', value: 1.16 },
	{ name: 'DataFactory', value: 0.87 },
	{ name: 'Compute', value: 0.63 },
	{ name: 'Synapse', value: 0.49 },
	{ name: 'AKS', value: 0.31 },
	{ name: 'OperationalInsights', value: 0.15 },
	{ name: 'Other Services', value: 0.34 },
];

const Dissection2026: React.FC = () => {
	const theme = useTheme();
	const colors = theme.palette as any;

	const cardStyle = {
		backgroundColor: `${colors.widget?.background?.card ?? '#FFFFFF'} !important`,
		borderRadius: '8px',
		p: '12px',
		minHeight: '280px',
		border: `1px solid ${colors.widget?.border?.main ?? '#E0E0E0'}`,
		boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
		display: 'flex',
		flexDirection: 'column' as const,
	};

	return (
		<PageWrapper sx={{ mt: '64px', pt: '36px !important', px: '32px !important' }}>
			{/* ─── Section 1: D&AI Overall Dissection ───────────────────────────── */}
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget?.text?.title ?? '#003771',
					fontWeight: 600,
				}}
			>
				D&AI Overall Dissection
			</Typography>

			<Grid container spacing={2} mb={3}>
				{/* Row 1 - Card 1 */}
				<Grid item xs={12} md={6}>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							color: colors.widget?.text?.title ?? '#003771',
                            mb: 2,
						}}
					>
						Cloud Vendor Breakdown
					</Typography>
					<CustomizedDataGrid
						tableColumns={cloudVendorColumns}
						tableData={cloudVendorTableData}
						tableType="cloudVendorDissection"
						defaultColumnNames={cloudVendorDefaultColumnNames}
						isFetching={false}
						isRenderPagination={false}
						isShowMoreButton={false}
						autoSetDraggedColumns={false}
						childRowClass={true}
						isLighterTheme={true}
					/>
				</Grid>

				{/* Row 1 - Card 2 */}
				<Grid item xs={12} md={6}>
					<Box sx={cardStyle}>
						<DissectionPie title="Azure Service Dissection FY2026" data={azurePieData} height={280} />
					</Box>
				</Grid>

				{/* Row 2 - Card 3 */}
				<Grid item xs={12} md={6}>
					<Box sx={cardStyle}>
						<DissectionPie title="AWS Service Dissection FY2026" data={awsPieData} height={280} />
					</Box>
				</Grid>

				{/* Row 2 - Card 4 */}
				<Grid item xs={12} md={6}>
					<Box sx={cardStyle}>
						<DissectionPie title="GCP Service Dissection FY2026" data={gcpPieData} height={280} />
					</Box>
				</Grid>
			</Grid>

			<Divider
				sx={{
					my: 3,
					borderColor: colors.widget?.border?.main ?? '#AABBD4',
				}}
			/>

			{/* ─── Section 2: EDF Dissection ───────────────────────────── */}
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget?.text?.title ?? '#003771',
					fontWeight: 600,
				}}
			>
				EDF Dissection
			</Typography>

			<Grid container spacing={2} mb={3}>
				{/* Chart 1: Prod */}
				<Grid item xs={12} md={6}>
					<Box sx={cardStyle}>
						<DissectionPie title="EDF Prod" data={prodPieData} height={280} />
					</Box>
				</Grid>

				{/* Chart 2: Non Prod */}
				<Grid item xs={12} md={6}>
					<Box sx={cardStyle}>
						<DissectionPie title="EDF Non Prod" data={nonProdPieData} height={280} />
					</Box>
				</Grid>
			</Grid>
		</PageWrapper>
	);
};

export default Dissection2026;
